import React, { useState } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { StaffApis } from "apis/staff";
import logoImage from "assets/logo/logo_icon_no_bg.png";

const LoginPage = () => {
    const [id, setId] = useState("");
    const [password, setPassword] = useState("");
    const navigate = useNavigate();

    const onLoginButtonClicked = () => {
        requestLogin();
    }

    const requestLogin = async () => {
        try {
            // const ret = await StaffClient.login(id, password);
            const ret = await StaffApis.login(id, password);
            toast.success("로그인에 성공했습니다.");
            if (ret.password_reset_required) {
                navigate("/password-reset");
            }else {
                navigate("/");
            }
            return ret;
        } catch (e) {
            console.error("[onLoginButtonClicked] error", e);
            toast.error("로그인에 실패했습니다.");
            return null;
        }
    }

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            onLoginButtonClicked();
        }
    }

    return (
        <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden" 
             style={{
                 background: '#0A1E3D',
                 fontFamily: "'Noto Sans KR', sans-serif"
             }}>
            
            {/* Floating Background Shapes */}
            <div className="absolute w-64 h-64 top-10 left-10 bg-white bg-opacity-10 rounded-full animate-pulse" 
                 style={{animation: 'float 6s ease-in-out infinite'}}></div>
            <div className="absolute w-32 h-32 top-1/3 right-20 bg-white bg-opacity-10 rounded-full animate-pulse" 
                 style={{animation: 'float 6s ease-in-out infinite', animationDelay: '2s'}}></div>
            <div className="absolute w-48 h-48 bottom-20 left-1/4 bg-white bg-opacity-10 rounded-full animate-pulse" 
                 style={{animation: 'float 6s ease-in-out infinite', animationDelay: '4s'}}></div>

            <div className="w-full max-w-md">
                {/* Logo Section */}
                <div className="flex items-center justify-center mb-8 gap-4">
                    <div className="flex items-center justify-center w-20 h-20 bg-white rounded-2xl shadow-lg p-0.5 flex-shrink-0">
                        <img src={logoImage} alt="유원ENF 로고" className="w-full h-full object-contain" />
                    </div>
                    <div className="flex flex-col">
                        <h1 className="text-3xl font-bold text-white">유원ENF</h1>
                        <p className="text-blue-100 text-sm mt-0.5">인트라넷 시스템</p>
                    </div>
                </div>

                {/* Login Card */}
                <div className="rounded-2xl p-8 shadow-2xl"
                     style={{
                         backdropFilter: 'blur(16px) saturate(180%)',
                         backgroundColor: 'rgba(255, 255, 255, 0.75)',
                         border: '1px solid rgba(209, 213, 219, 0.3)',
                         animation: 'slideUp 0.6s ease-out'
                     }}>
                    <form className="space-y-6" onKeyDown={handleKeyDown}>
                        {/* ID Input */}
                        <div>
                            <label htmlFor="userid" className="block text-sm font-medium text-gray-700 mb-2">
                                아이디
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                                    </svg>
                                </div>
                                <input 
                                    type="text" 
                                    id="userid" 
                                    name="userid" 
                                    value={id}
                                    onChange={(e) => setId(e.target.value?.toLowerCase())}
                                    required 
                                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-700 focus:border-transparent transition-all duration-200"
                                    placeholder="아이디를 입력하세요"
                                />
                            </div>
                        </div>

                        {/* Password Input */}
                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                                비밀번호
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                                    </svg>
                                </div>
                                <input 
                                    type="password" 
                                    id="password" 
                                    name="password" 
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required 
                                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-700 focus:border-transparent transition-all duration-200"
                                    placeholder="비밀번호를 입력하세요"
                                />
                            </div>
                        </div>

                        {/* Login Button */}
                        <div>
                            <button 
                                type="button"
                                onClick={onLoginButtonClicked}
                                className="login-btn group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-700 hover:transform hover:-translate-y-0.5 transition-all duration-300"
                                style={{
                                    background: '#0A1E3D',
                                }}
                            >
                                <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                                    <svg className="h-5 w-5 text-blue-200 group-hover:text-blue-100 transition-colors duration-200" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd"/>
                                    </svg>
                                </span>
                                로그인
                            </button>
                        </div>
                    </form>
                </div>

                {/* Footer */}
                <div className="mt-8 text-center">
                    <p className="text-sm text-blue-100">
                        © 2025 유원ENF. All rights reserved.
                    </p>
                </div>
            </div>

            <style>{`
                @keyframes float {
                    0%, 100% { transform: translateY(0px); }
                    50% { transform: translateY(-20px); }
                }
                
                @keyframes slideUp {
                    from {
                        opacity: 0;
                        transform: translateY(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                .login-btn:hover {
                    box-shadow: 0 10px 25px rgba(102, 126, 234, 0.3) !important;
                }
            `}</style>
        </div>
    )
}

export default LoginPage;